import React from 'react';
import { BlurUpImage } from '../../../shared/components/BlurUpImage';
import {
  MARKETING_PLACEHOLDERS,
  MARKETING_DOMINANT_COLORS,
} from '../../../shared/marketing-placeholders';

/**
 * Release Planning section — two-part composition.
 *
 * Left:  Premium framed SVG Release Roadmap showing 6 synthetic releases on a
 *        horizontal timeline (Gantt-style). Matches the real Relix Release Roadmap
 *        product moment: colored bars, milestone markers for the current release,
 *        a "Current" badge, and a dark product-window chrome.
 *
 * Right: Compact capacity / usage table using the same synthetic release IDs
 *        so both visuals tell a consistent story.
 *
 * Synthetic data is consistent across both sides (prompt requirement):
 *   4.08.01 78d 78%  · 4.08.02 78d 82%  · 4.08.03 78d 87%
 *   4.08.04 78d 91%  · 4.08.05 64d 74%  · 4.08.06 78d 0%
 */

// ── Shared data ──────────────────────────────────────────────────────────────

type ReleaseRow = {
  id: string;
  capacity: string;
  usage: number;
  tone: 'good' | 'warn' | 'future';
};

const ROWS: readonly ReleaseRow[] = [
  { id: '4.08.01', capacity: '78d', usage: 78, tone: 'good' },
  { id: '4.08.02', capacity: '78d', usage: 82, tone: 'good' },
  { id: '4.08.03', capacity: '78d', usage: 87, tone: 'good' },
  { id: '4.08.04', capacity: '78d', usage: 91, tone: 'warn' },
  { id: '4.08.05', capacity: '64d', usage: 74, tone: 'good' },
  { id: '4.08.06', capacity: '78d', usage: 0, tone: 'future' },
] as const;

// ── SVG Roadmap ───────────────────────────────────────────────────────────────

/**
 * Gantt-style horizontal timeline. Each release occupies its own row.
 * Positions are expressed as fractions of the bar-area width (504px).
 *
 * 4.08.01–4.08.04 are completed/near-complete (short sequential bars).
 * 4.08.05 is the current major release (long violet bar + orange regression segment).
 * 4.08.06 is the upcoming release (muted future bar).
 */

type RoadmapBar = {
  id: string;
  rowIdx: number;
  startFrac: number; // 0–1 within bar area
  endFrac: number;
  tone: 'done' | 'warn' | 'current' | 'future';
  regStartFrac?: number; // start of regression segment inside current bar
  current?: boolean;
};

const BARS: readonly RoadmapBar[] = [
  { id: '4.08.01', rowIdx: 0, startFrac: 0.0, endFrac: 0.208, tone: 'done' },
  { id: '4.08.02', rowIdx: 1, startFrac: 0.175, endFrac: 0.383, tone: 'done' },
  { id: '4.08.03', rowIdx: 2, startFrac: 0.344, endFrac: 0.552, tone: 'done' },
  { id: '4.08.04', rowIdx: 3, startFrac: 0.512, endFrac: 0.702, tone: 'warn' },
  {
    id: '4.08.05',
    rowIdx: 4,
    startFrac: 0.0,
    endFrac: 0.587,
    tone: 'current',
    regStartFrac: 0.505,
    current: true,
  },
  { id: '4.08.06', rowIdx: 5, startFrac: 0.542, endFrac: 1.0, tone: 'future' },
] as const;

const DATE_TICKS = ['Apr 13', 'Apr 26', 'May 10', 'May 24', 'Jun 07'] as const;

function _ReleaseRoadmapSVG(): React.ReactElement {
  // Layout constants
  const W = 580;
  const LABEL_W = 62;
  const GAP = 6;
  const BAR_X = LABEL_W + GAP; // 68
  const BAR_W = W - BAR_X - 6; // ~506
  const ROW_H = 21;
  const BAR_H = 15;
  const ROW_GAP = 6;
  const FIRST_ROW_Y = 50; // leaves room for date axis at y≈32

  const rowY = (idx: number) => FIRST_ROW_Y + idx * (ROW_H + ROW_GAP);
  const barY = (idx: number) => rowY(idx) + Math.round((ROW_H - BAR_H) / 2);

  const bx = (frac: number) => BAR_X + frac * BAR_W;

  const totalH = FIRST_ROW_Y + 6 * (ROW_H + ROW_GAP) + 10; // ≈202

  return (
    <svg
      viewBox={`0 0 ${W} ${totalH}`}
      preserveAspectRatio="xMidYMid meet"
      aria-hidden="true"
      focusable="false"
      className="release-plan-roadmap__svg"
    >
      <defs>
        {/* Done bars */}
        <linearGradient id="rp-done" x1="0%" y1="0%" x2="100%" y2="0%">
          <stop offset="0%" stopColor="rgba(74,222,128,0.55)" />
          <stop offset="100%" stopColor="rgba(34,211,238,0.45)" />
        </linearGradient>
        {/* Warn bar */}
        <linearGradient id="rp-warn" x1="0%" y1="0%" x2="100%" y2="0%">
          <stop offset="0%" stopColor="rgba(251,191,36,0.6)" />
          <stop offset="100%" stopColor="rgba(250,204,21,0.45)" />
        </linearGradient>
        {/* Current bar main (violet) */}
        <linearGradient id="rp-current" x1="0%" y1="0%" x2="100%" y2="0%">
          <stop offset="0%" stopColor="rgba(124,92,255,0.85)" />
          <stop offset="100%" stopColor="rgba(100,74,220,0.9)" />
        </linearGradient>
        {/* Current bar regression (orange) */}
        <linearGradient id="rp-reg" x1="0%" y1="0%" x2="100%" y2="0%">
          <stop offset="0%" stopColor="rgba(251,146,60,0.82)" />
          <stop offset="100%" stopColor="rgba(249,115,22,0.7)" />
        </linearGradient>
        {/* Future bar */}
        <linearGradient id="rp-future" x1="0%" y1="0%" x2="100%" y2="0%">
          <stop offset="0%" stopColor="rgba(124,92,255,0.28)" />
          <stop offset="100%" stopColor="rgba(124,92,255,0.12)" />
        </linearGradient>
      </defs>

      {/* ── Date axis ────────────────────────────────────────────────── */}
      {DATE_TICKS.map((label, i) => {
        const x = bx(i / (DATE_TICKS.length - 1));
        return (
          <g key={label}>
            <line
              x1={x}
              y1={28}
              x2={x}
              y2={FIRST_ROW_Y + 6 * (ROW_H + ROW_GAP)}
              stroke="rgba(124,92,255,0.1)"
              strokeWidth="1"
              strokeDasharray="3 3"
            />
            <text
              x={x}
              y={22}
              textAnchor="middle"
              fill="rgba(180,189,218,0.72)"
              fontSize="8"
              fontFamily="var(--font-body, system-ui)"
              letterSpacing="0.04em"
            >
              {label}
            </text>
          </g>
        );
      })}

      {/* ── Current-date marker ──────────────────────────────────────── */}
      {(() => {
        const cx = bx(0.505);
        return (
          <line
            x1={cx}
            y1={30}
            x2={cx}
            y2={barY(4) + BAR_H + 2}
            stroke="rgba(255,255,255,0.28)"
            strokeWidth="1.2"
            strokeDasharray="4 3"
          />
        );
      })()}

      {/* ── Release bars ─────────────────────────────────────────────── */}
      {BARS.map((bar) => {
        const x = bx(bar.startFrac);
        const w = bx(bar.endFrac) - x;
        const y = barY(bar.rowIdx);
        const r = BAR_H / 2;

        // Label
        const labelColor =
          bar.tone === 'done'
            ? 'rgba(134,239,172,0.92)'
            : bar.tone === 'warn'
              ? 'rgba(253,224,71,0.9)'
              : bar.tone === 'current'
                ? 'rgba(220,210,255,0.95)'
                : 'rgba(167,139,250,0.6)';

        return (
          <g key={bar.id}>
            {/* Release ID label */}
            <text
              x={LABEL_W - 4}
              y={y + BAR_H / 2 + 3}
              textAnchor="end"
              fill={labelColor}
              fontSize="9"
              fontFamily="var(--font-display, system-ui)"
              fontWeight="700"
              letterSpacing="-0.01em"
            >
              {bar.id}
            </text>

            {/* Bar track (background) */}
            <rect x={x} y={y} width={w} height={BAR_H} rx={r} fill="rgba(15,22,38,0.5)" />

            {bar.tone === 'current' && bar.regStartFrac !== undefined ? (
              <>
                {/* Main portion: violet */}
                <rect
                  x={x}
                  y={y}
                  width={bx(bar.regStartFrac) - x}
                  height={BAR_H}
                  rx={r}
                  fill="url(#rp-current)"
                />
                {/* Regression segment: orange */}
                <rect
                  x={bx(bar.regStartFrac)}
                  y={y}
                  width={bx(bar.endFrac) - bx(bar.regStartFrac)}
                  height={BAR_H}
                  rx={2}
                  fill="url(#rp-reg)"
                />
              </>
            ) : bar.tone === 'future' ? (
              <rect
                x={x}
                y={y}
                width={w}
                height={BAR_H}
                rx={r}
                fill="url(#rp-future)"
                stroke="rgba(124,92,255,0.3)"
                strokeWidth="1"
                strokeDasharray="3 2"
              />
            ) : (
              <rect
                x={x}
                y={y}
                width={w}
                height={BAR_H}
                rx={r}
                fill={bar.tone === 'done' ? 'url(#rp-done)' : 'url(#rp-warn)'}
              />
            )}

            {/* "Current" badge on 4.08.05 */}
            {bar.current ? (
              <g>
                <rect
                  x={x + 6}
                  y={y + 3}
                  width={42}
                  height={9}
                  rx={4.5}
                  fill="rgba(255,255,255,0.18)"
                />
                <text
                  x={x + 27}
                  y={y + 10}
                  textAnchor="middle"
                  fill="rgba(255,255,255,0.9)"
                  fontSize="6"
                  fontFamily="var(--font-body, system-ui)"
                  fontWeight="700"
                  letterSpacing="0.1em"
                >
                  CURRENT
                </text>
              </g>
            ) : null}

            {/* Milestone markers on the current release */}
            {bar.current && bar.regStartFrac !== undefined ? (
              <>
                {/* Freeze Code marker */}
                <g>
                  <line
                    x1={bx(bar.regStartFrac)}
                    y1={y - 10}
                    x2={bx(bar.regStartFrac)}
                    y2={y}
                    stroke="rgba(251,146,60,0.7)"
                    strokeWidth="1.2"
                  />
                  <polygon
                    points={`${bx(bar.regStartFrac)},${y - 11} ${bx(bar.regStartFrac) - 4},${y - 17} ${bx(bar.regStartFrac) + 4},${y - 17}`}
                    fill="rgba(251,146,60,0.7)"
                  />
                  <text
                    x={bx(bar.regStartFrac)}
                    y={y - 19}
                    textAnchor="middle"
                    fill="rgba(253,186,116,0.88)"
                    fontSize="6.5"
                    fontFamily="var(--font-body, system-ui)"
                  >
                    Regression
                  </text>
                </g>
                {/* Start Dev marker */}
                <g>
                  <line
                    x1={x + 2}
                    y1={y - 10}
                    x2={x + 2}
                    y2={y}
                    stroke="rgba(167,139,250,0.6)"
                    strokeWidth="1"
                  />
                  <text
                    x={x + 2}
                    y={y - 12}
                    textAnchor="start"
                    fill="rgba(196,196,255,0.72)"
                    fontSize="6.5"
                    fontFamily="var(--font-body, system-ui)"
                  >
                    Start Dev
                  </text>
                </g>
              </>
            ) : null}
          </g>
        );
      })}

      {/* ── Legend ───────────────────────────────────────────────────── */}
      <g transform={`translate(${BAR_X}, ${totalH - 8})`}>
        <rect x="0" y="-4" width="10" height="6" rx="3" fill="url(#rp-current)" />
        <text x="13" y="1" fill="rgba(180,189,218,0.72)" fontSize="7" fontFamily="var(--font-body)">
          Current
        </text>
        <rect x="58" y="-4" width="10" height="6" rx="3" fill="url(#rp-reg)" />
        <text x="71" y="1" fill="rgba(180,189,218,0.72)" fontSize="7" fontFamily="var(--font-body)">
          Regression
        </text>
        <rect x="134" y="-4" width="10" height="6" rx="3" fill="url(#rp-done)" />
        <text
          x="147"
          y="1"
          fill="rgba(180,189,218,0.72)"
          fontSize="7"
          fontFamily="var(--font-body)"
        >
          Done
        </text>
        <rect
          x="174"
          y="-4"
          width="10"
          height="6"
          rx="3"
          fill="url(#rp-future)"
          stroke="rgba(124,92,255,0.3)"
          strokeWidth="1"
          strokeDasharray="2 1"
        />
        <text
          x="187"
          y="1"
          fill="rgba(180,189,218,0.72)"
          fontSize="7"
          fontFamily="var(--font-body)"
        >
          Upcoming
        </text>
      </g>
    </svg>
  );
}

// ── Capacity table ────────────────────────────────────────────────────────────

function ReleaseCapacityTable(): React.ReactElement {
  return (
    <aside className="release-plan-mini__rows" aria-label="Release capacity overview">
      <header className="release-plan-mini__rows-head">
        <span className="release-plan-mini__rows-col release-plan-mini__rows-col--version">
          Release
        </span>
        <span className="release-plan-mini__rows-col release-plan-mini__rows-col--cap">
          Capacity
        </span>
        <span className="release-plan-mini__rows-col release-plan-mini__rows-col--usage">
          Usage
        </span>
      </header>
      <ul>
        {ROWS.map((r) => (
          <li
            key={r.id}
            className={`release-plan-mini__row release-plan-mini__row--${
              r.tone === 'future' ? 'bad' : r.tone === 'warn' ? 'warn' : 'good'
            }`}
          >
            <span className="release-plan-mini__rows-col release-plan-mini__rows-col--version">
              {r.id}
            </span>
            <span className="release-plan-mini__rows-col release-plan-mini__rows-col--cap">
              {r.capacity}
            </span>
            <span className="release-plan-mini__rows-col release-plan-mini__rows-col--usage">
              <span className="release-plan-mini__usage-track">
                <span className="release-plan-mini__usage-fill" style={{ width: `${r.usage}%` }} />
              </span>
              <span className="release-plan-mini__usage-pct">{r.usage}%</span>
            </span>
          </li>
        ))}
      </ul>
    </aside>
  );
}

// ── Public export ─────────────────────────────────────────────────────────────

export function ReleasePlanCard(): React.ReactElement {
  return (
    <div className="release-plan-card">
      {/* Left: premium framed roadmap */}
      <div
        className="release-plan-card__frame product-window"
        role="img"
        aria-label="Relix Release Roadmap showing six sequential releases on a horizontal timeline"
      >
        <div className="release-plan-card__roadmap-viewport">
          <BlurUpImage
            src="/brand/solutions/roadmap-moment.png"
            webpSrc="/brand/solutions/roadmap-moment-original.webp"
            placeholderSrc={MARKETING_PLACEHOLDERS['roadmap-moment']}
            dominantColor={MARKETING_DOMINANT_COLORS['roadmap-moment']}
            alt="Relix Release Roadmap — 4.08.05 current release bar with Freeze Code and Regression milestones, 4.08.06 upcoming release bar on a horizontal timeline"
            width={1024}
            height={576}
            className="release-plan-card__roadmap-shot"
          />
          <span className="release-plan-card__roadmap-veil" aria-hidden="true" />
        </div>
      </div>

      {/* Right: compact capacity / usage table */}
      <ReleaseCapacityTable />
    </div>
  );
}
